import { useEffect } from "react";
import type { Map as MapLibreMap } from "maplibre-gl";
import type { Route } from "@wondermaps/maps";

interface RouteLayerProps {
  route: Route;
  map: MapLibreMap;
  /** Index of completed segment (-1 if none completed) */
  completedSegmentIndex?: number;
}

const COMPLETED_SOURCE_ID = "wayfinding-route-completed";
const UPCOMING_SOURCE_ID = "wayfinding-route-upcoming";
const COMPLETED_LINE_LAYER_ID = "wayfinding-route-completed-line";
const UPCOMING_LINE_LAYER_ID = "wayfinding-route-upcoming-line";
const START_MARKER_LAYER_ID = "wayfinding-start-marker";
const END_MARKER_LAYER_ID = "wayfinding-end-marker";

/**
 * Renders a wayfinding route on the map
 * - Dashed blue line for route path (faded for completed, full opacity for upcoming)
 * - Blue dot at start (user location)
 * - Destination marker at end
 */
export function RouteLayer({ route, map, completedSegmentIndex = -1 }: RouteLayerProps) {
  useEffect(() => {
    if (!map || !route || route.path.length < 2) return;

    const startPoint = route.path[0];
    const endPoint = route.path[route.path.length - 1];

    // Split route into completed and upcoming segments
    // completedSegmentIndex is the index of the segment the user is currently on
    // Segments 0 to completedSegmentIndex-1 are fully completed
    // We include points 0 through completedSegmentIndex for completed path
    const splitIndex = Math.max(0, completedSegmentIndex);

    // Completed path: from start up to and including the current segment start
    // Need at least 2 points to make a line
    const completedCoords = splitIndex > 0
      ? route.path.slice(0, splitIndex + 1)
      : [];

    // Upcoming path: from current segment start to end
    const upcomingCoords = route.path.slice(splitIndex);

    // GeoJSON for completed route
    const completedGeoJson: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: completedCoords.length >= 2
        ? [{
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: completedCoords,
            },
          }]
        : [],
    };

    // GeoJSON for upcoming route
    const upcomingGeoJson: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: upcomingCoords.length >= 2
        ? [{
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: upcomingCoords,
            },
          }]
        : [],
    };

    // GeoJSON for start marker
    const startGeoJson: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Point",
            coordinates: startPoint,
          },
        },
      ],
    };

    // GeoJSON for end marker
    const endGeoJson: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Point",
            coordinates: endPoint,
          },
        },
      ],
    };

    // Add or update completed route source
    const existingCompletedSource = map.getSource(COMPLETED_SOURCE_ID);
    if (existingCompletedSource) {
      (existingCompletedSource as maplibregl.GeoJSONSource).setData(completedGeoJson);
    } else {
      map.addSource(COMPLETED_SOURCE_ID, {
        type: "geojson",
        data: completedGeoJson,
      });
    }

    // Add or update upcoming route source
    const existingUpcomingSource = map.getSource(UPCOMING_SOURCE_ID);
    if (existingUpcomingSource) {
      (existingUpcomingSource as maplibregl.GeoJSONSource).setData(upcomingGeoJson);
    } else {
      map.addSource(UPCOMING_SOURCE_ID, {
        type: "geojson",
        data: upcomingGeoJson,
      });
    }

    // Add or update start marker source
    const startSourceId = `${COMPLETED_SOURCE_ID}-start`;
    const existingStartSource = map.getSource(startSourceId);
    if (existingStartSource) {
      (existingStartSource as maplibregl.GeoJSONSource).setData(startGeoJson);
    } else {
      map.addSource(startSourceId, {
        type: "geojson",
        data: startGeoJson,
      });
    }

    // Add or update end marker source
    const endSourceId = `${UPCOMING_SOURCE_ID}-end`;
    const existingEndSource = map.getSource(endSourceId);
    if (existingEndSource) {
      (existingEndSource as maplibregl.GeoJSONSource).setData(endGeoJson);
    } else {
      map.addSource(endSourceId, {
        type: "geojson",
        data: endGeoJson,
      });
    }

    // Add completed route line layer (faded - 30% opacity)
    if (!map.getLayer(COMPLETED_LINE_LAYER_ID)) {
      map.addLayer({
        id: COMPLETED_LINE_LAYER_ID,
        type: "line",
        source: COMPLETED_SOURCE_ID,
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#4285f4", // Google blue
          "line-width": 5,
          "line-dasharray": [2, 2],
          "line-opacity": 0.3,
        },
      });
    }

    // Add upcoming route line layer (full opacity)
    if (!map.getLayer(UPCOMING_LINE_LAYER_ID)) {
      map.addLayer({
        id: UPCOMING_LINE_LAYER_ID,
        type: "line",
        source: UPCOMING_SOURCE_ID,
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#4285f4", // Google blue
          "line-width": 5,
          "line-dasharray": [2, 2],
          "line-opacity": 1,
        },
      });
    }

    // Add start marker layer (blue dot - same as user location)
    if (!map.getLayer(START_MARKER_LAYER_ID)) {
      map.addLayer({
        id: START_MARKER_LAYER_ID,
        type: "circle",
        source: startSourceId,
        paint: {
          "circle-color": "#4285f4",
          "circle-radius": 8,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 3,
        },
      });
    }

    // Add end marker layer (destination icon - red marker)
    if (!map.getLayer(END_MARKER_LAYER_ID)) {
      map.addLayer({
        id: END_MARKER_LAYER_ID,
        type: "circle",
        source: endSourceId,
        paint: {
          "circle-color": "#ea4335", // Google red
          "circle-radius": 10,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 3,
        },
      });
    }

    // Cleanup on unmount — wrapped in try/catch to handle map already removed
    return () => {
      try {
        if (map.getLayer(COMPLETED_LINE_LAYER_ID)) map.removeLayer(COMPLETED_LINE_LAYER_ID);
        if (map.getLayer(UPCOMING_LINE_LAYER_ID)) map.removeLayer(UPCOMING_LINE_LAYER_ID);
        if (map.getLayer(START_MARKER_LAYER_ID)) map.removeLayer(START_MARKER_LAYER_ID);
        if (map.getLayer(END_MARKER_LAYER_ID)) map.removeLayer(END_MARKER_LAYER_ID);
        if (map.getSource(COMPLETED_SOURCE_ID)) map.removeSource(COMPLETED_SOURCE_ID);
        if (map.getSource(UPCOMING_SOURCE_ID)) map.removeSource(UPCOMING_SOURCE_ID);
        if (map.getSource(startSourceId)) map.removeSource(startSourceId);
        if (map.getSource(endSourceId)) map.removeSource(endSourceId);
      } catch {
        // Map may already be removed (e.g. component unmount race)
      }
    };
  }, [map, route, completedSegmentIndex]);

  return null;
}
